<template>
  <div id="tours-driver"></div>
</template>
<script setup lang="ts">
import { driver, type Config, type DriveStep, type Driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import type { TOURS_CODES } from '~/constants'
import { TOURS_TYPES } from '~/constants'
import useAuthStore from '~/store/auth'
import useUiStore from '@/store/ui'

const TYPES_CONFIG = {
  [TOURS_TYPES.NEW_FEATURE]: {
    mainTitle: '🚀 New Feature!',
  },
  [TOURS_TYPES.UPDATE]: {
    mainTitle: '🚀 Update!',
  },
  [TOURS_TYPES.INIT_USER]: {
    mainTitle: '🚀 Welcome aboard!',
  },
}

const props = withDefaults(
  defineProps<{
    steps?: DriveStep[]
    tour: TOURS_CODES
    config?: Config
    showWelcomeAboard?: boolean
    type?: TOURS_TYPES
    highlight?: DriveStep
    fullWidthButton?: boolean
    onlyHighlight?: boolean
  }>(),
  {
    steps: () => [],
    highlight: undefined,
    config: undefined,
    type: undefined,
    fullWidthButton: false,
    onlyHighlight: false,
  },
)

const driverObj = ref<Driver | null>(null)

defineExpose({
  driverObj,
})

const authStore = useAuthStore()
const { isAuth, tours } = storeToRefs(authStore)

const uiStore = useUiStore()

const { data: currentUser } = useCurrentUser(isAuth.value)

const handleStart = async () => {
  if (driverObj.value && props.highlight && props.type) {
    driverObj.value?.highlight({
      ...props.highlight,
      popover: {
        nextBtnText: 'Learn more',
        prevBtnText: 'Dismiss',
        showButtons: ['next', 'previous', 'close'],
        onPopoverRender: popover => {
          popover.title.innerHTML = `
            <div class="flex flex-col pb-2 border-b-[1.5px] border-solid border-black-10">
                <b class="text-black-100 text-subhead-2 !font-sans">${TYPES_CONFIG[props.type!].mainTitle}</b>
                <p class="!text-black-80 !text-body-2 ml-5 mt-0.5">${props.highlight?.popover?.title}</p
            </div>`
          if (props.fullWidthButton) {
            popover.footerButtons.querySelector('.driver-popover-next-btn')?.classList.add('!w-full', '!ml-0')
          }
        },
        onCloseClick: () => {
          driverObj.value?.destroy()
        },
        onPrevClick: () => {
          driverObj.value?.destroy()
        },
        onNextClick: () => {
          if (!props.onlyHighlight) {
            driverObj.value?.drive()
          }
        },
        ...props.highlight.popover,
      },
    })
  } else {
    await nextTick()

    lock()

    driverObj.value?.drive()
  }
}

const checkIsTourShown = () => {
  if (currentUser.value && (!uiStore.getIsTourCompleted(props.tour) || authStore.getIsTourActive(props.tour))) {
    handleStart()
  }
}

watch(
  tours,
  () => {
    checkIsTourShown()
  },
  {
    immediate: true,
  },
)

const { lock, unlock } = useBodyScrollLock()

const { mutateAsync: update } = useUpdateTourStatus()

const { catchErrors } = useErrorHandler()

const finishTour = () => {
  update(props.tour).catch(catchErrors)
  authStore.cleanActiveTourId()
}

onMounted(() => {
  driverObj.value = driver({
    steps: props.steps,
    stageRadius: 16,
    stagePadding: 16,
    allowClose: false,
    disableActiveInteraction: true,
    onPopoverRender: popover => {
      popover.footer.classList.remove('driver-popover-footer')
      popover.closeButton.style.display = ''
    },
    onDestroyed: () => {
      finishTour()

      unlock()
    },
    ...props.config,
  })
  checkIsTourShown()
})

onBeforeUnmount(() => {
  if (driverObj.value) {
    driverObj.value.destroy()
  }
})
</script>

<style scoped lang="scss">
@import '@/styles/variables.scss';

.tour {
  width: 48px;
  height: 48px;
  position: fixed;
  bottom: 40px;
  right: 40px;

  &__trigger-button {
    width: 48px;
    height: 48px;
    z-index: 3;
    border-radius: 50% !important;
    padding: 8px !important;
    box-shadow: $box-shadow-floating !important;
  }

  &__popup {
    :deep() {
      .popup__content {
        padding: 32px 24px;

        &-buttons {
          justify-content: center;
        }
      }
    }
  }
}
</style>
